import { track } from '@vercel/analytics';

import type {
  AnalyticsEventName,
  AnalyticsEventPayloadMap,
  AnalyticsProperties,
  AnalyticsPropertyValue,
} from './events.types';

/**
 * Determines whether the application is running in development mode.
 */
const isDevelopment = (): boolean => process.env.NODE_ENV === 'development';

/**
 * Determines whether a value is supported by Vercel Analytics.
 */
const isAnalyticsPropertyValue = (value: unknown): value is AnalyticsPropertyValue =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

/**
 * Converts event-specific data into the flat property record required by
 * Vercel Analytics.
 *
 * Undefined values, arrays, functions and nested objects are omitted.
 */
const toAnalyticsProperties = (eventData: object): AnalyticsProperties => {
  const analyticsProperties: AnalyticsProperties = {};

  for (const [propertyName, propertyValue] of Object.entries(eventData)) {
    if (isAnalyticsPropertyValue(propertyValue)) {
      analyticsProperties[propertyName] = propertyValue;
    }
  }

  return analyticsProperties;
};

/**
 * Tracks a strongly typed analytics event.
 *
 * The required event-data fields are inferred from the selected event name.
 * Developers can create the event-data object separately without importing
 * payload types, using `as const`, or using `satisfies`.
 *
 * @param eventName - Registered event name.
 * @param eventData - Data required by the selected event.
 *
 * @example
 * ```ts
 * const eventData = {
 *   page: window.location.pathname,
 *   location: 'floating_button',
 * };
 *
 * trackEvent(
 *   ANALYTICS_EVENTS.WHATSAPP_CLICKED,
 *   eventData,
 * );
 * ```
 *
 * @example
 * ```ts
 * const eventData = {
 *   page: window.location.pathname,
 *   calendly_url: calendlyUrl,
 * };
 *
 * trackEvent(
 *   ANALYTICS_EVENTS.BOOK_A_DEMO_CLICKED,
 *   eventData,
 * );
 * ```
 */
export function trackEvent<EventName extends AnalyticsEventName>(
  eventName: EventName,
  eventData: AnalyticsEventPayloadMap[EventName],
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const analyticsProperties = toAnalyticsProperties(eventData);

  if (isDevelopment()) {
    console.info('[Analytics]', {
      eventName,
      eventData: analyticsProperties,
    });
  }

  try {
    track(eventName, analyticsProperties);
  } catch (error: unknown) {
    if (isDevelopment()) {
      console.warn(`[Analytics] Failed to track "${eventName}"`, error);
    }
  }
}
